import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { TurnTranscript, SpeakingRole } from '@db8/shared'
import { Button } from '@/components/ui/button'
import { Trash2, Pencil, Check } from 'lucide-react'
import { Textarea } from '@/components/ui/textarea'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { toMarkdown, toMarkdownForView } from '@/lib/transcriptFormat'
import ReactMarkdown from 'react-markdown'
import StrongAsTag from './StrongAsTag'

type TranscriptProps = {
  transcript: TurnTranscript
  onDelete: (step: number, transcript: TurnTranscript, noPublish?: boolean) => void
  onEdit: (step: number, updatedTranscript: TurnTranscript) => void
  autoEdit?: boolean
  setEditingId: Dispatch<SetStateAction<string>>
}

export const Transcript = ({
  transcript,
  onDelete,
  onEdit,
  autoEdit,
  setEditingId,
}: TranscriptProps) => {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(transcript.text)
  const { isModerator: userIsModerator, currentUser } = useHallUsers()
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (autoEdit) {
      setEditing(true)
      setTimeout(() => textareaRef.current?.focus(), 0)
    }
  }, [autoEdit])

  const step = transcript.turnStep
  const role = transcript.role
  const isModerator = role === SpeakingRole.Moderator
  const isAffirmative = role === SpeakingRole.Affirmative
  const isNegative = role === SpeakingRole.Negative

  const containerClass = isModerator
    ? 'justify-center'
    : isAffirmative
      ? 'justify-start'
      : 'justify-end'

  const borderClass = isModerator
    ? 'border-gray-300 dark:border-gray-600'
    : isAffirmative
      ? 'border-green-300 dark:border-green-600'
      : 'border-blue-300 dark:border-blue-600'

  const textAlignClass = isModerator ? 'text-center' : isAffirmative ? 'text-left' : 'text-right'

  const user = transcript?.hallUser
  const displayName = isModerator
    ? `${user?.displayName || 'Unknown'} - Moderator`
    : user?.displayName || 'Unknown Speaker'

  const handleSave = () => {
    setEditing(false)
    onEdit(step, { ...transcript, text })
    setEditingId(null)
  }

  const handleClear = () => {
    setEditing(false)
    setEditingId(null)
    setText(transcript.text)
    if (transcript.text === '') onDelete(step, transcript, true)
  }

  return (
    <div className={`relative flex ${containerClass}`}>
      <div
        className={`p-3 my-1 rounded-md shadow-md border-2 ${borderClass} ${
          editing ? 'w-full' : ''
        } bg-gray-100 dark:bg-gray-800 text-foreground dark:text-white dark:[&_*]:text-white`}
      >
        <div className="flex w-full items-center justify-between">
          <div
            className={`text-xs font-semibold text-gray-600 dark:text-gray-300 ${textAlignClass}`}
          >
            {displayName}
          </div>
          {(userIsModerator || transcript.hallUser?.id === currentUser?.id) && !editing && (
            <div className="flex gap-2 items-center ml-4">
              <Button
                variant="ghost"
                onClick={() => setEditing(true)}
                title="Edit"
                className="rounded-full p-1 text-blue-600 dark:text-blue-400 hover:bg-transparent bg-background mt-[-10px] mr-[-10px] w-[25px] h-[25px]"
              >
                <Pencil size={15} />
              </Button>
              <Button
                variant="ghost"
                onClick={() => onDelete(step, transcript)}
                title="Delete"
                className="rounded-full p-1 text-black dark:text-white hover:text-red-600 hover:bg-transparent bg-background mt-[-10px] mr-[-10px] w-[25px] h-[25px]"
              >
                <Trash2 size={15} />
              </Button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="flex flex-col">
            <Textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="text-sm mt-2 noScrollbar w-full bg-gray-200 dark:bg-background text-foreground dark:text-white border-gray-300 dark:border-gray-600"
            />
            <div className="flex w-full justify-end items-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setText(toMarkdown(text))}>
                Format
              </Button>
              <Button variant="destructive" onClick={handleClear}>
                Clear
              </Button>
              <Button variant="secondary" onClick={handleSave}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className={`text-sm ${isModerator ? 'text-center' : ''}`}>
            <div className="whitespace-pre-wrap leading-6 space-y-2">
              <ReactMarkdown
                components={{
                  strong: StrongAsTag,
                }}
              >
                {toMarkdownForView(text)}
              </ReactMarkdown>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
